import { memo, useEffect, useMemo, useRef } from 'react';
import type { CompanionManifest } from '@/lib/companion/types';
import type { FrameStore } from '../types/frame';
import { resolveAnimation, pingPongFrame, pingPongSpan } from '../utils/animation-map';
import { useDevicePixelRatio } from '../hooks/use-device-pixel-ratio';
import styles from './companion-sprite.module.css';

const DEFAULT_SCALE = 0.33;
/** Above this, sleep with a timer instead of burning rAF until the cell is due. */
const RAF_THRESHOLD_MS = 16;

export interface CompanionSpriteProps {
  manifest: CompanionManifest;
  frames: FrameStore;
  scale?: number;
}

function CompanionSpriteImpl({ manifest, frames, scale = DEFAULT_SCALE }: CompanionSpriteProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const pixelRatio = useDevicePixelRatio();

  const { width: frameW, height: frameH } = manifest.frame;
  const drawW = Math.round(frameW * scale);
  const drawH = Math.round(frameH * scale);
  const backingW = Math.max(1, Math.round(drawW * pixelRatio));
  const backingH = Math.max(1, Math.round(drawH * pixelRatio));

  const image = useMemo(() => {
    const img = new Image();
    img.src = manifest.spritesheet;
    return img;
  }, [manifest.spritesheet]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let rafId = 0;
    let timerId = 0;
    let stopped = false;
    let frame = 0;
    let lastStep = performance.now();
    let drawnFrame = -1;
    let drawnRow = -1;
    let drawnMirror = false;
    let drawnRotate = false;
    let activeRow = '';
    let cycleEnded = false;

    const paused = () => document.visibilityState === 'hidden';

    const clear = () => {
      if (rafId !== 0) { window.cancelAnimationFrame(rafId); rafId = 0; }
      if (timerId !== 0) { window.clearTimeout(timerId); timerId = 0; }
    };

    const schedule = (delayMs = 0) => {
      if (stopped || paused() || rafId !== 0 || timerId !== 0) return;

      if (delayMs > RAF_THRESHOLD_MS) {
        timerId = window.setTimeout(() => { timerId = 0; schedule(); }, delayMs);
        return;
      }
      rafId = window.requestAnimationFrame(render);
    };

    const kick = () => {
      if (stopped || paused()) return;
      if (timerId !== 0) { window.clearTimeout(timerId); timerId = 0; }
      schedule();
    };

    const render = (now: number) => {
      rafId = 0;
      if (stopped || paused()) return;

      const { state, direction } = frames.get();
      const anim = resolveAnimation(manifest, state, direction);
      const count = Math.max(1, anim.frames);

      // Reset on any change to how the row is played, not just which row it
      // is. A one-shot pose resolving to the row a ping-pong was already
      // running would otherwise inherit a counter past its own last frame,
      // clamp there on the first pass, and fire onCycleEnd immediately.
      const signature = `${anim.row}:${anim.frames}:${anim.loop}:${anim.pingPong ?? false}`;
      if (signature !== activeRow) {
        activeRow = signature;
        frame = 0;
        lastStep = now;
        drawnFrame = -1;
        cycleEnded = false;
      }

      const stepMs = manifest.loopMs / count;
      if (!anim.loop && frame >= count - 1) {
        frame = count - 1;
        if (!cycleEnded) {
          cycleEnded = true;
          frames.get().onCycleEnd?.();
        }
      } else {
        if (now - lastStep >= stepMs) {
          frame += 1;
          lastStep = now;
        }
        frame %= pingPongSpan(count, anim.pingPong);
      }

      if (!image.complete || image.naturalWidth <= 0) {
        schedule(stepMs);
        return;
      }

      const column = pingPongFrame(frame, count, anim.pingPong);

      if (
        column !== drawnFrame
        || anim.row !== drawnRow
        || anim.mirror !== drawnMirror
        || anim.rotate !== drawnRotate
      ) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.save();

        if (anim.rotate) {
          ctx.translate(backingW / 2, backingH / 2);
          ctx.rotate(-Math.PI / 2);
          ctx.translate(-backingW / 2, -backingH / 2);
        }
        if (anim.mirror) {
          ctx.translate(backingW, 0);
          ctx.scale(-1, 1);
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(
          image,
          column * frameW, anim.row * frameH, frameW, frameH,
          0, 0, backingW, backingH,
        );
        ctx.restore();

        drawnFrame = column;
        drawnRow = anim.row;
        drawnMirror = anim.mirror;
        drawnRotate = anim.rotate;
      }

      // A held one-shot has no next cell to draw. Sleeping here instead of
      // re-entering rAF is what keeps a settled pose — sleep, most of all —
      // from costing a display-rate loop that redraws nothing. The frame-store
      // subscription and the visibility handler both kick() the loop awake.
      if (cycleEnded) return;

      schedule(Math.max(0, stepMs - (now - lastStep)));
    };

    const onVisibility = () => {
      clear();
      if (paused()) return;
      lastStep = performance.now();
      drawnFrame = -1;
      kick();
    };

    const unsubscribe = frames.subscribe(kick);
    image.addEventListener('load', kick);
    document.addEventListener('visibilitychange', onVisibility);
    schedule();

    return () => {
      stopped = true;
      clear();
      unsubscribe();
      image.removeEventListener('load', kick);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [image, manifest, frames, frameW, frameH, backingW, backingH]);

  return (
    <canvas
      ref={canvasRef}
      className={styles.canvas}
      width={backingW}
      height={backingH}
      aria-label={manifest.displayName}
      style={{ '--companion-w': `${drawW}px`, '--companion-h': `${drawH}px` } as React.CSSProperties}
    />
  );
}

/** Memoized so a parent re-render alone never re-runs the canvas setup. The
 *  canvas is rebuilt when the companion or its scale changes — both are real
 *  changes to what is drawn, not incidental renders. */
export const CompanionSprite = memo(CompanionSpriteImpl);
